import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, MessageCircle, User } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';

const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasUnreadMessages, hasNewMatches } = useNotifications();

  const navItems = [
    {
      id: 'swipe',
      icon: 'logo',
      label: '',
      path: '/tenant/swipe',
    },
    {
      id: 'matches',
      icon: Heart,
      label: 'Matches',
      path: '/tenant/matches',
    },
    {
      id: 'chat',
      icon: MessageCircle,
      label: 'Chats',
      path: '/tenant/chat',
    },
    {
      id: 'profile',
      icon: User,
      label: 'Profile',
      path: '/tenant/profile',
    },
  ];

  const isActive = (path: string) => {
    if (path === '/tenant/chat') {
      return location.pathname.startsWith('/tenant/chat');
    }
    if (path === '/tenant/matches') {
      return location.pathname.startsWith('/tenant/matches');
    }
    return location.pathname === path;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-100 shadow-2xl md:hidden">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const active = isActive(item.path);
          const isLogo = item.icon === 'logo';
          const hasNotification =
            (item.id === 'matches' && hasNewMatches) || (item.id === 'chat' && hasUnreadMessages);
          const label = item.label || 'Discover';

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              aria-label={hasNotification ? `${label} (new)` : label}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 ${
                active ? 'text-primary-300' : 'text-gray-400'
              }`}
            >
              <div className={`relative transition-all duration-300 ${active ? 'scale-110' : 'scale-100'}`}>
                {isLogo ? (
                  <img 
                    src="/logo.png" 
                    alt="RoomZa" 
                    className={`w-12 h-12 rounded-lg transition-all duration-300 ${active ? 'scale-110' : ''}`}
                  />
                ) : (
                  <>
                    {React.createElement(item.icon as any, {
                      className: `w-6 h-6 ${active ? 'fill-primary-300' : ''}`,
                      strokeWidth: active ? 2.5 : 2
                    })}
                    {/* Notification Badge */}
                    {item.id === 'matches' && hasNewMatches && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full border-2 border-white shadow-lg animate-pulse" />
                    )}
                    {item.id === 'chat' && hasUnreadMessages && (
                      <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-gradient-to-r from-primary-300 to-primary-400 rounded-full border-2 border-white shadow-lg animate-pulse" />
                    )}
                  </>
                )}
                {active && !isLogo && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-300 rounded-full"></div>
                )}
              </div>
              <span className={`text-xs font-semibold mt-1 ${active ? 'text-primary-300' : 'text-gray-500'}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
